import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './use-auth'

export type BookingHours = Record<string, [string, string][]>

export interface Company {
  id: string
  /** 014. Percent added to card payments only. Null or 0 means no surcharge. Capped at 2.5 by the database. */
  card_surcharge_pct?: number | null
  booking_enabled?: boolean
  booking_slug?: string | null
  booking_timezone?: string
  booking_hours?: BookingHours
  booking_lead_time_min?: number
  booking_horizon_days?: number
  booking_max_per_day?: number
  booking_buffer_min?: number
  booking_slot_step_min?: number
  name: string | null
  abn: string | null
  phone: string | null
  email: string | null
  logo: string | null
  address: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  country: string | null
  accent_color: string | null
  gst_registered: boolean | null
  default_tax_rate: number | null
  quote_prefix: string | null
  invoice_prefix: string | null
  default_terms: string | null
  payment_terms_days: number | null
  auto_create_job_on_accept: boolean | null
  google_review_url: string | null
  review_request_enabled: boolean | null
  review_channel: string | null
  review_request_template_id: number | null
  review_request_statuses: string[] | null
  review_request_delay_days: number | null
  target_margin_pct: number | null
  service_reminder_enabled: boolean | null
  service_reminder_enabled_at: string | null
  service_reminder_lead_days: number | null
  email_monthly_limit: number | null
  sms_credits: number | null
  sms_reminders_enabled: boolean | null
  quote_followups_enabled: boolean | null
  crew_can_view_all_jobs: boolean | null
  invoice_reminders_enabled: boolean | null
  deposit_default_pct: number | null
  subscription_type: string | null
  subscription_status: string | null
  subscription_expires_at: string | null
  stripe_subscription_id: string | null
  max_users: number | null
  inbound_token: string | null
  inbound_quote_enabled: boolean | null
  ai_quote_included: number | null
  ai_quote_used: number | null
  ai_quote_topups: number | null
  bank_account_name: string | null
  bank_bsb: string | null
  bank_account_number: string | null
  payment_instructions: string | null
  payment_qr_url: string | null
  /** Spec 035. Empty array = the SWMS feature is dormant for this tenant and nothing changes. */
  hrcw_categories?: string[]
  swms_enforcement?: 'warn' | 'block'
  /** 048 T006/T007 — the health gate's posture, and the per-day run ceiling. */
  health_check_mode?: 'warn' | 'block'
  workflow_daily_run_cap?: number | null
}

const COLS =
  'id, name, abn, phone, email, logo, address, city, state, postal_code, country, accent_color, gst_registered, default_tax_rate, quote_prefix, invoice_prefix, default_terms, payment_terms_days, auto_create_job_on_accept, google_review_url, review_request_enabled, review_channel, review_request_template_id, review_request_statuses, review_request_delay_days, target_margin_pct, service_reminder_enabled, service_reminder_enabled_at, service_reminder_lead_days, email_monthly_limit, sms_credits, sms_reminders_enabled, quote_followups_enabled, invoice_reminders_enabled, deposit_default_pct, subscription_type, subscription_status, subscription_expires_at, stripe_subscription_id, max_users, inbound_token, inbound_quote_enabled, ai_quote_included, ai_quote_used, ai_quote_topups, bank_account_name, bank_bsb, bank_account_number, payment_instructions, payment_qr_url, booking_enabled, booking_slug, booking_timezone, booking_hours, booking_lead_time_min, booking_horizon_days, booking_max_per_day, booking_buffer_min, booking_slot_step_min, hrcw_categories, swms_enforcement, crew_can_view_all_jobs, health_check_mode, workflow_daily_run_cap, card_surcharge_pct'

export function useCompany() {
  const { companyId } = useAuth()
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: ['company', companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<Company | null> => {
      const { data, error } = await supabase.from('company').select(COLS).eq('id', companyId!).maybeSingle()
      if (error) throw error
      return data as Company | null
    },
  })

  const save = useMutation({
    mutationFn: async (patch: Partial<Company>) => {
      const { error } = await supabase.from('company').update(patch).eq('id', companyId!)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company'] }),
  })

  return { company: query.data ?? null, isLoading: query.isLoading, save }
}

export interface TeamMember {
  id: string
  first_name: string | null
  last_name: string | null
  email: string | null
  role: string | null
  phone: string | null
  is_verified: boolean | null
  hourly_cost: number | null
  payroll_id: string | null
}

export function useTeam() {
  const { companyId } = useAuth()
  const list = useQuery({
    queryKey: ['team', companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<TeamMember[]> => {
      // users.company_id is the tenant FK → scope the team to the current company.
      const { data, error } = await supabase
        .from('users')
        .select('id, first_name, last_name, email, role, phone, is_verified, hourly_cost, payroll_id')
        .eq('company_id', companyId!)
        .is('deleted_at', null)
        .order('first_name', { ascending: true })
      if (error) throw error
      return (data ?? []) as TeamMember[]
    },
  })
  return { members: list.data ?? [], isLoading: list.isLoading }
}
